import { useState } from 'react';
import { CalendarClock, FilePlus2, TriangleAlert, UserRoundPen } from 'lucide-react';
import { BoutonPrincipal, BoutonSecondaire, Pastille } from '../ui/Primitives';
import { useToast } from '../ui/useToast';
import { ErreurApi } from '../../lib/api';
import { detailsRefus } from '../../lib/erreurs-api';
import { usePreparerFactureHeuresMensuelles } from '../../lib/useFactures';
import type { FacturationMensuelle as Facturation, MoisFacture } from '../../lib/useFinancements';

/** Les mois les plus récents d'abord ; les plus anciens se déplient. */
const MOIS_AFFICHES = 6;

function messageRefus(erreur: unknown, defaut: string): string {
  const detail = detailsRefus(erreur)[0];
  if (detail) return detail.message;
  return erreur instanceof ErreurApi ? erreur.message : defaut;
}

/**
 * Facturation mensuelle d'un contrat de professionnalisation (TF-416) : chaque
 * mois commencé de l'accord, ses heures réalisées — déclarées par les
 * formateurs (TF-421), émargées avant leur mise en service — avec le repère
 * émargé et qui les a déclarées, ce qui est facturé et ce qui reste à
 * facturer, et le geste qui prépare la facture du mois, ou son complément. Le
 * cumul se lit face au volume de l'accord. Tout — état, montants, refus — vient
 * de l'API.
 */
export function FacturationMensuelle({
  accordId,
  facturation,
}: {
  accordId: string;
  facturation: Facturation;
}): JSX.Element {
  const [tous, setTous] = useState(false);
  const { cumul, mois } = facturation;
  const visibles = tous ? mois : mois.slice(0, MOIS_AFFICHES);
  const caches = mois.length - visibles.length;

  return (
    <section className="rounded-2xl border border-gray-100" aria-label="Facturation mensuelle aux heures">
      <div className="flex flex-wrap items-center justify-between gap-2 border-b border-gray-100 px-3 py-2.5 sm:px-4">
        <h5 className="flex min-w-0 items-center gap-2 text-sm font-bold text-codialis-navy">
          <CalendarClock className="h-4 w-4 shrink-0 text-codialis-sky" aria-hidden />
          Facturation mensuelle
        </h5>
        <span className="text-[11px] text-codialis-gray">Heures : {facturation.source}</span>
      </div>

      <div className="space-y-3 p-3 sm:p-4">
        <div className="rounded-xl bg-codialis-sand px-3 py-2.5 sm:px-4">
          <div className="flex flex-wrap items-baseline justify-between gap-x-3 gap-y-1">
            <p className="text-sm font-semibold text-codialis-navy">
              {cumul.heuresFacturees} facturées
              {cumul.volume ? <span className="font-normal text-codialis-gray"> sur {cumul.volume}</span> : null}
            </p>
            <p className="text-sm font-semibold tabular-nums text-codialis-navy">{cumul.montantFacture}</p>
          </div>
          {cumul.part !== null && (
            <div
              className="mt-2 h-2 overflow-hidden rounded-full bg-white"
              role="progressbar"
              aria-label="Part du volume de l’accord facturée"
              aria-valuemin={0}
              aria-valuemax={100}
              aria-valuenow={cumul.part}
            >
              <div className="h-full rounded-full bg-codialis-sky" style={{ width: `${cumul.part}%` }} />
            </div>
          )}
          {cumul.reste && <p className="mt-1 text-[11px] text-codialis-gray">Reste {cumul.reste} dans le volume de l’accord.</p>}
        </div>

        {facturation.message && (
          <p className="rounded-xl bg-amber-50 px-3 py-2 text-xs font-semibold text-amber-800">{facturation.message}</p>
        )}

        {mois.length > 0 && (
          <ul className="space-y-2">
            {visibles.map((un) => (
              <LigneMois key={un.cle} accordId={accordId} mois={un} />
            ))}
          </ul>
        )}

        <div className="flex flex-wrap items-center justify-between gap-2">
          <p className="min-w-0 text-[11px] text-codialis-gray">
            {facturation.moisAVenir === 0
              ? mois.length > 0
                ? 'Tous les mois de l’accord sont commencés.'
                : null
              : facturation.moisAVenir === 1
                ? '1 mois de l’accord est à venir.'
                : `${facturation.moisAVenir} mois de l’accord sont à venir.`}
          </p>
          {caches > 0 && (
            <BoutonSecondaire onClick={() => setTous(true)}>
              Afficher {caches === 1 ? 'le mois précédent' : `les ${caches} mois précédents`}
            </BoutonSecondaire>
          )}
        </div>
      </div>
    </section>
  );
}

function LigneMois({ accordId, mois }: { accordId: string; mois: MoisFacture }): JSX.Element {
  const toast = useToast();
  const preparer = usePreparerFactureHeuresMensuelles();
  const [erreur, setErreur] = useState<string | null>(null);
  const refus = mois.preparation?.refus ?? null;
  const complement = mois.etat === 'COMPLEMENT';

  const lancer = (): void => {
    setErreur(null);
    preparer.mutate(
      { accordId, mois: mois.cle },
      {
        onSuccess: () =>
          toast(`Facture ${complement ? 'complémentaire ' : ''}de ${mois.libelle.toLowerCase()} préparée : vérifiez-la avant de l’émettre.`),
        onError: (motif) => setErreur(messageRefus(motif, 'La facture n’a pas pu être préparée.')),
      },
    );
  };

  return (
    <li className="min-w-0 rounded-xl border border-gray-100 bg-white px-3 py-2.5 sm:px-4">
      <div className="flex flex-wrap items-start justify-between gap-x-3 gap-y-1.5">
        <div className="min-w-0 flex-1 basis-40">
          <p className="text-sm font-bold text-codialis-navy">{mois.libelle}</p>
          {mois.partiel && <p className="text-[11px] text-codialis-gray">{mois.periode}</p>}
        </div>
        <Pastille texte={mois.etatLibelle} ton={mois.ton} />
      </div>

      <dl className="mt-2 grid grid-cols-3 gap-2 text-xs">
        <div className="min-w-0">
          <dt className="text-[11px] text-codialis-gray">Réalisées</dt>
          <dd className="font-semibold tabular-nums text-codialis-navy">
            {mois.heuresRealisees}
            {mois.heuresEmargees && (
              <span className="block font-normal text-codialis-gray">{mois.heuresEmargees} émargées</span>
            )}
          </dd>
        </div>
        <div className="min-w-0">
          <dt className="text-[11px] text-codialis-gray">Facturées</dt>
          <dd className="font-semibold tabular-nums text-codialis-navy">
            {mois.facture ? (
              <>
                {mois.facture.heures}
                <span className="block font-normal text-codialis-gray">{mois.facture.montant}</span>
              </>
            ) : (
              '—'
            )}
          </dd>
        </div>
        <div className="min-w-0">
          <dt className="text-[11px] text-codialis-gray">À facturer</dt>
          <dd className="font-semibold tabular-nums text-codialis-navy">
            {mois.aFacturer ? (
              <>
                {mois.aFacturer.heures}
                {mois.aFacturer.montant && (
                  <span className="block font-normal text-codialis-gray">{mois.aFacturer.montant}</span>
                )}
              </>
            ) : (
              '—'
            )}
          </dd>
        </div>
      </dl>

      {mois.facture && mois.facture.numeros.length > 0 && !mois.detail?.includes(mois.facture.numeros[0] ?? '') && (
        <p className="mt-1 break-words text-[11px] text-codialis-gray">
          <span className="font-mono">{mois.facture.numeros.join(', ')}</span>
        </p>
      )}
      {mois.origine && (
        <p className="mt-1 flex gap-1.5 text-[11px] text-codialis-gray">
          <UserRoundPen className="mt-px h-3.5 w-3.5 shrink-0" aria-hidden />
          <span className="min-w-0 break-words">{mois.origine}</span>
        </p>
      )}
      {mois.declarationsAttendues && mois.etat !== 'EN_ATTENTE_DECLARATION' && (
        <p className="mt-1 flex gap-1.5 text-[11px] font-semibold text-amber-700">
          <TriangleAlert className="mt-px h-3.5 w-3.5 shrink-0" aria-hidden />
          <span className="min-w-0 break-words">{mois.declarationsAttendues}</span>
        </p>
      )}
      {mois.feuillesOuvertes && (
        <p className="mt-1 flex gap-1.5 text-[11px] font-semibold text-amber-700">
          <TriangleAlert className="mt-px h-3.5 w-3.5 shrink-0" aria-hidden />
          <span className="min-w-0">{mois.feuillesOuvertes} : leurs heures ne sont pas encore comptées.</span>
        </p>
      )}
      {mois.detail && (
        <p
          className={`mt-1 break-words text-xs ${
            mois.etat === 'EN_ATTENTE_DECLARATION' ? 'font-semibold text-amber-700' : 'text-codialis-gray'
          }`}
        >
          {mois.detail}
        </p>
      )}

      {mois.preparation && (
        <div className="mt-2 flex flex-wrap items-center justify-end gap-x-3 gap-y-1.5">
          {refus && <p className="min-w-0 flex-1 text-xs text-codialis-gray">{refus}</p>}
          <BoutonPrincipal onClick={lancer} disabled={refus !== null || preparer.isPending}>
            <FilePlus2 className="mr-1.5 h-4 w-4" aria-hidden />
            {preparer.isPending ? 'Préparation…' : complement ? 'Préparer le complément' : 'Préparer la facture'}
          </BoutonPrincipal>
        </div>
      )}
      {erreur && (
        <p role="alert" className="mt-2 rounded-lg bg-red-50 px-2.5 py-1.5 text-xs font-semibold text-red-700">
          {erreur}
        </p>
      )}
    </li>
  );
}
